import React from 'react';

import { FormLabel } from '@components/form-label';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import {
  useJourneyIntegrationEndpoints,
  useJourneyIntegrationWebhook,
} from 'api/integrations/hooks';
import {
  useCreateJourneyActionRelationship,
  useJourneyAction,
  useJourneyActionRelationships,
  useJourneyActions,
  useRemoveJourneyActionRelationship,
} from 'api/journey-actions/hooks';
import { useVersionPreview } from 'api/journeys/hooks';
import { JourneyAction } from 'api/types';
import {
  ActionDuplicateButton,
  ActionHeader,
  ActionRemoveButton,
  ActionSidebarLoading,
  SidebarProps,
} from 'ui/journey-builder/node-types/common';

import {
  getRelationshipOptions,
  DirectLine,
  ignoreTypes,
  RelationshipLine,
  SmallActionCard,
} from './common';

import { ACTION_TYPES } from '../action-config';

const ACTION_TYPE = ACTION_TYPES.journey_merge;

type RelationshipAction = {
  shortId: string | null;
  actionData: JourneyAction;
  title?: string;
};

const findParentAction = (actions: any[], shortId: string | undefined): any | undefined => {
  if (!shortId) return undefined;

  const action = actions.find((a) => a.shortId === shortId);
  if (!action || !['journey_branch', 'branch_base'].includes(action.action)) return action;

  return findParentAction(actions, action.parentAction?.shortId);
};

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<typeof ACTION_TYPE>(journeyActionId);
  const { t } = useScopedTranslate(sidebarLocale);
  const publishedVersion = data?.publishedVersion ?? null;
  const journeyActionsQuery = useJourneyActions(data?.journey ?? '', {
    enabled: Boolean(data?.journey) && !publishedVersion,
  });
  const versionActionsQuery = useVersionPreview(data?.journey ?? '', publishedVersion);
  const relationshipsQuery = useJourneyActionRelationships(journeyActionId);
  const integrationsQuery = useJourneyIntegrationEndpoints(data?.journey ?? '');
  const webhooksQuery = useJourneyIntegrationWebhook(data?.journey ?? '');

  const integrations = integrationsQuery.data ?? [];
  const webhooks = webhooksQuery.data ?? [];

  const addMutation = useCreateJourneyActionRelationship();
  const removeMutation = useRemoveJourneyActionRelationship();

  const actions = publishedVersion ? versionActionsQuery.data : journeyActionsQuery.data;

  if (isLoading || !relationshipsQuery.data || !data || !actions) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }

  const parentAction = findParentAction(actions, data.parentAction?.shortId);

  const relationshipActions: RelationshipAction[] = [];

  const relationshipShortIds = relationshipsQuery.data.actions.map(
    (relationship) => relationship.parentAction
  );

  relationshipsQuery.data.actions.forEach((relationship) => {
    const action = actions.find(
      (journeyAction) => journeyAction.shortId === relationship.parentAction
    );
    if (action) {
      relationshipActions.push({
        shortId: relationship.shortId,
        actionData: action,
        title: action.description,
      });
    }
  });

  const ancestors_short_ids = data.ancestors.map((ancestor) => ancestor[0]);

  const validMergeActions = actions.filter(
    (journeyAction) =>
      !ignoreTypes.includes(journeyAction.action) &&
      journeyAction.shortId !== journeyActionId &&
      !ancestors_short_ids.includes(journeyAction.shortId) &&
      !data.descendants.includes(journeyAction.shortId) &&
      !relationshipShortIds.includes(journeyAction.shortId)
  );

  const handleSave = ({
    parentShortId,
    childShortId,
  }: {
    parentShortId: string;
    childShortId: string;
  }) => {
    return addMutation.mutateAsync({ parentShortId, childShortId, journeyId: data.journey });
  };
  const relationshipOptions = getRelationshipOptions(
    validMergeActions,
    integrations,
    webhooks,
    t('noDescription')
  );

  return (
    <Sidebar>
      <ActionHeader actionType={ACTION_TYPE} {...props} />
      <Sidebar.Content>
        <Stack space={6}>
          {parentAction && (
            <>
              <Stack space={4}>
                <FormLabel text={t('previous')} helpText={t('previousHelpText')} />
                <DirectLine />
                <SmallActionCard
                  key={parentAction.shortId}
                  actionData={parentAction}
                  integrations={integrations}
                  webhooks={webhooks}
                  description={parentAction.description || t('noDescription')}
                />
                <Text text={t('previousNeeds')} font="regular14" />
              </Stack>

              <Stack space={4} orientation="horizontal" style={{ width: '100%' }} align="center">
                <Separator />
                <Text font="semibold14" color="textHelper" text={t('core.and')} />
                <Separator />
              </Stack>
            </>
          )}

          <Stack space={4}>
            {relationshipActions.length > 0 && (
              <>
                <Text text={t('selectedActions')} font="semibold14" />
                <RelationshipLine />
                <Stack space={2}>
                  {relationshipActions.map((action) => (
                    <SmallActionCard
                      key={action.shortId}
                      actionData={action.actionData}
                      integrations={integrations}
                      webhooks={webhooks}
                      description={action.title || t('noDescription')}
                      onRemove={
                        action.shortId
                          ? () =>
                              removeMutation.mutateAsync({
                                relationshipShortId: action.shortId!,
                                childShortId: journeyActionId,
                                journeyId: data.journey,
                              })
                          : undefined
                      }
                    />
                  ))}
                </Stack>
                <Text text={t('selectedActionsNeeds')} font="regular14" />
              </>
            )}

            <Select
              noOptions={() => t('noRelationships')}
              label={t('addRelationship')}
              options={relationshipOptions}
              hasFilterText
              onChange={(option) =>
                option && handleSave({ childShortId: journeyActionId, parentShortId: option })
              }
            />
          </Stack>
        </Stack>
      </Sidebar.Content>
      <Sidebar.Footer>
        <Stack space={2} orientation="horizontal">
          <ActionRemoveButton action={data} />
          <ActionDuplicateButton journeyActionShortId={data.shortId} />
        </Stack>
      </Sidebar.Footer>
    </Sidebar>
  );
};

const sidebarLocale = {
  en: {
    noRelationships: 'No available actions',
    previous: 'Previous action',
    previousNeeds: 'Needs to be completed or manually skipped',
    previousHelpText: '(In workflow pipeline)',
    selectedActions: 'Selected actions',
    selectedActionsNeeds: 'Need to be completed or manually / conditionally skipped',
    addRelationship: 'Add action',
    noDescription: 'No description',
  },
};
